import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import SanYsidroBolsContainer from '../../containers/billing/SanYsidroBols';
import AuthUserContainer from '../../containers/AuthUser';

import { getSharedStyles } from '../../themes';
import EnhancedTable from '../../components/table/EnhancedTable';
import { Typography } from '@material-ui/core';

import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import SanYsidroBolUploadDialog from '../../components/Dialogs/SanYsidroBolUploadDialog';
import { grey, blue } from '@material-ui/core/colors';
import BolLinkedStatus from '../../components/billing/BolLinkedStatus';

import invoiceIcon from '../../images/invoicePdf.svg';

const UPLOAD_BOL_ENABLED = true;

const INTAKE_TYPE_ENUM = {
	manual: 'manual',
	emailIngestion: 'emailIngestion',
};

const intakeTypeToDisplayMap = {
	[INTAKE_TYPE_ENUM.manual]: 'Manual',
	[INTAKE_TYPE_ENUM.emailIngestion]: 'Email Ingestion',
};

const intakeTypeToColorMap = {
	[INTAKE_TYPE_ENUM.manual]: grey[500],
	[INTAKE_TYPE_ENUM.emailIngestion]: blue[500],
};

function intakeTypeToDisplay(intakeType) {
	return intakeTypeToDisplayMap[intakeType] || 'Manual';
}

const styles = (theme) => ({
	...getSharedStyles(theme),
	uploadButton: {
		borderRadius: 100,
	},
	invoiceIcon: {
		display: 'inline-block',
		width: 40,
		height: 48,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
});

const columns = [
	{
		sortId: 'sanYsidroBolId',
		displayId: 'sanYsidroBolId',
		label: 'ID',
	},
	{
		sortId: 'bolNumber',
		displayId: 'bolNumber',
		label: 'BOL #',
	},
	{
		sortId: 'railcarNumber',
		displayId: 'railcarNumber',
		label: 'Railcar #',
	},
	{
		sortId: 'loadingNumber',
		displayId: 'loadingNumber',
		label: 'Loading #',
	},
	{
		sortId: 'destinationStationNumber',
		displayId: 'destinationDisplay',
		label: 'Destination',
	},
	{
		sortId: 'startDateTime',
		displayId: 'startFinishDisplay',
		label: 'Start/Finish',
	},
	{
		sortId: 'netGallons',
		displayId: 'gallonsDisplay',
		label: 'Gallons',
	},
	{
		noSort: true,
		sortId: 'bolPdfResourceId',
		displayId: 'bolPdfDisplay',
		label: 'PDF',
	},
	{
		sortId: 'intakeType',
		displayId: 'intakeDisplay',
		label: 'Intake',
	},
	{
		sortId: 'linkedStationDeliveryId',
		displayId: 'linkedStationDeliveryDisplay',
		label: 'Linked Delivery',
	},
	{
		sortId: 'linkedStatus',
		displayId: 'linkedStatusDisplay',
		label: 'Linked Status',
	},
];

class SanYsidroBols extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'sanYsidroBolId',
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			isUploadOpen: false,
		};
	}

	componentDidMount() {
		const { sanYsidroBols } = this.props;

		if (!sanYsidroBols) {
			this.refreshTable();
		} else {
			this.formatSanYsidroBols();
		}
	}

	componentDidUpdate(prevProps) {
		const { sanYsidroBols } = this.props;
		if (prevProps.sanYsidroBols !== sanYsidroBols) {
			this.formatSanYsidroBols();
		}
	}

	refreshTable = () => {
		this.props.sanYsidroBolSearch({
			afterDateTime: moment().subtract(2, 'months').format('YYYY-MM-DD'),
		});
	};

	formatSanYsidroBols = () => {
		const { classes, sanYsidroBols, stationMap, bulkDeliveryMap } = this.props;

		const rows = [];
		(sanYsidroBols || []).forEach((sanYsidroBol) => {
			const { sanYsidroBolId, startDateTime, finishDateTime, dateCreated } = sanYsidroBol;

			sanYsidroBol.key = sanYsidroBolId;
			sanYsidroBol.startDateTimeFormatted = startDateTime
				? moment(startDateTime).format('M/D/YY h:mm a')
				: '-';
			sanYsidroBol.finishDateTimeFormatted = finishDateTime
				? moment(finishDateTime).format('M/D/YY h:mm a')
				: '-';
			sanYsidroBol.dateCreatedFormatted = dateCreated
				? moment(dateCreated).format('M/D/YY h:mm a')
				: '-';

			sanYsidroBol.startFinishDisplay = (
				<div>
					<div>Start: {sanYsidroBol.startDateTimeFormatted}</div>
					<div>Finish: {sanYsidroBol.finishDateTimeFormatted}</div>
				</div>
			);

			sanYsidroBol.gallonsDisplay = (
				<div>
					<div>Net: {sanYsidroBol.netGallons}</div>
					<div>Gross: {sanYsidroBol.grossGallons}</div>
				</div>
			);

			// ETOH BOLs are destined for a terminal (e.g. "KM-MSN VLY"), which
			// doesn't parse as a station number — show the raw destination text.
			sanYsidroBol.destinationDisplay =
				sanYsidroBol.destinationStationNumber || sanYsidroBol.destinationRaw || '-';

			sanYsidroBol.bolPdfDisplay = sanYsidroBol.bolPdfResourceId ? (
				<div
					className={classes.invoiceIcon}
					onClick={() => {
						this.handlePdfClick(sanYsidroBol.bolPdfResourceId);
					}}
				/>
			) : (
				'-'
			);

			sanYsidroBol.linkedStatusDisplay = (
				<BolLinkedStatus
					linkedStatus={sanYsidroBol.linkedStatus}
					message={sanYsidroBol.linkedErrorMessage}
				/>
			);

			sanYsidroBol.intakeDisplay = (
				<div>
					<div style={{ color: intakeTypeToColorMap[sanYsidroBol.intakeType], fontWeight: 'bold' }}>
						Type: {intakeTypeToDisplay(sanYsidroBol.intakeType)}
					</div>
					<div>Date Created: {sanYsidroBol.dateCreatedFormatted}</div>
				</div>
			);

			if (sanYsidroBol.linkedStationDeliveryId) {
				const station = stationMap[sanYsidroBol.linkedStationId] || { number: 'Missing' };
				sanYsidroBol.linkedStationDeliveryDisplay = (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>
							Station Delivery #
							<a
								href={`/logistics/stationDelivery/update/${sanYsidroBol.linkedStationDeliveryId}`}
								style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<strong>{sanYsidroBol.linkedStationDeliveryId}</strong>
							</a>
						</div>
						<div>
							Station: {station.number} / Tank: {sanYsidroBol.linkedTankNumber}
						</div>
					</div>
				);
			} else if (sanYsidroBol.linkedBulkDeliveryId) {
				const bulkDelivery = (bulkDeliveryMap || {})[sanYsidroBol.linkedBulkDeliveryId];
				sanYsidroBol.linkedStationDeliveryDisplay = (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>
							Bulk Delivery #
							<a
								href={`/logistics/bulkDelivery/update/${sanYsidroBol.linkedBulkDeliveryId}`}
								style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<strong>{sanYsidroBol.linkedBulkDeliveryId}</strong>
							</a>
						</div>
						{bulkDelivery && <div>Product: {bulkDelivery.productType}</div>}
					</div>
				);
			}

			rows.push(sanYsidroBol);
		});
		this.setState({ rows });
	};

	handleOpenUpload = () => {
		this.setState({ isUploadOpen: true });
	};

	handleCloseUpload = () => {
		this.setState({ isUploadOpen: false });
	};

	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	render() {
		const { classes, resourceMap, sanYsidroBolUpload, showErrors } = this.props;
		const { rows, order, orderBy } = this.state;

		if (!rows) {
			return <div>Loading the San Ysidro BOLs...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={8}>
							<Typography variant="h5" align="left" gutterBottom className={classes.title}>
								<span>San Ysidro BOLs</span>
							</Typography>
						</Grid>
						<Grid item xs={4} style={{ textAlign: 'right' }}>
							<Button
								variant="outlined"
								color="primary"
								className={classes.uploadButton}
								style={{ marginRight: 10 }}
								onClick={this.refreshTable}
							>
								Refresh
							</Button>
							{UPLOAD_BOL_ENABLED && (
								<Button
									variant="contained"
									color="primary"
									className={classes.uploadButton}
									onClick={this.handleOpenUpload}
								>
									Upload BOL
								</Button>
							)}
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<EnhancedTable
							columns={columns}
							rows={rows}
							defaultOrder={order}
							defaultOrderBy={orderBy}
						/>

						<ResourcePopup
							open={this.state.isResourcePopupOpen}
							onClose={this.handleCloseResourcePopup}
							resourceIds={this.state.resourceIdsPoppedUp}
							resourceMap={resourceMap}
						/>
					</Paper>

					{UPLOAD_BOL_ENABLED && (
						<SanYsidroBolUploadDialog
							open={this.state.isUploadOpen}
							onClose={this.handleCloseUpload}
							uploadBol={sanYsidroBolUpload}
							showErrors={showErrors}
							onAfterSave={this.refreshTable}
						/>
					)}
				</Grid>
			</Grid>
		);
	}
}

SanYsidroBols.propTypes = {
	classes: PropTypes.object.isRequired,
	sanYsidroBolSearch: PropTypes.func.isRequired,
	sanYsidroBolUpload: PropTypes.func.isRequired,
	sanYsidroBols: PropTypes.array,

	showErrors: PropTypes.func.isRequired,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,
	bulkDeliveryMap: PropTypes.object,
	terminalMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	SanYsidroBolsContainer,
])(SanYsidroBols);
